import { useLayoutEffect, useRef, useState } from 'react';

import { cutToWords, fittingWords, words } from './clampText.js';

interface ClampedText {
  /** What to render: the whole paragraph, or as much of it as the lines hold. */
  text: string;
  /** Whether anything was cut, and so whether there is a toggle to show. */
  truncated: boolean;
}

interface ClampedTextHandle extends ClampedText {
  /** Goes on a hidden paragraph styled exactly like the visible one. */
  probeRef: (node: HTMLParagraphElement | null) => void;
}

/**
 * Cut prose to a line count, at a word boundary, leaving room for the toggle on
 * the last line.
 *
 * Measured rather than clamped in CSS: `-webkit-line-clamp` owns the end of the
 * last line, which leaves "Read more" nowhere to go but a third line — spending
 * the line the clamp just saved — or floating over the text behind a gradient,
 * which dissolves half a word and reads as a rendering fault rather than as a
 * control. Cutting the string puts the toggle in the flow: the sentence runs
 * out, then the link.
 *
 * The search is over words, not characters: "Illinois" and "wwwwwwww" are the
 * same length and not the same width, so any characters-per-line constant is
 * wrong for one of them. Measurements happen against a hidden probe so the live
 * paragraph is never seen mid-search.
 *
 * Where there is no layout to measure — jsdom, or before the probe mounts — the
 * paragraph renders whole and offers no toggle. Showing everything is the safe
 * end of that failure: the reader loses a tidy two-line card, not the reasoning.
 */
export function useClampedText(
  text: string,
  open: boolean,
  lines = 2,
  alwaysToggle = false,
): ClampedTextHandle {
  const [fit, setFit] = useState<ClampedText>({ text, truncated: false });
  const probe = useRef<HTMLParagraphElement | null>(null);

  // Measured against the text and the width, never against `open`: whether the
  // reader expanded it is not a property of how much fits. Resetting the fit when
  // `open` flipped made "Read more" a one-way door — the toggle it printed
  // disappeared, and the paragraph could not be collapsed again.
  useLayoutEffect(() => {
    const node = probe.current;
    if (!node || !text) {
      setFit({ text, truncated: false });
      return;
    }

    const all = words(text);
    const lineHeight = parseFloat(getComputedStyle(node).lineHeight);
    // No line height to reason about (jsdom, or a font still loading) means no
    // trustworthy measurement, so nothing is cut.
    if (!Number.isFinite(lineHeight) || lineHeight <= 0) {
      setFit({ text, truncated: false });
      return;
    }
    const room = lineHeight * lines + 1; // +1 absorbs sub-pixel rounding

    // The toggle shares the last line, so it is measured with the text rather
    // than after it — otherwise the cut fits and the toggle pushes a third line.
    const fits = (count: number): boolean => {
      const cut = cutToWords(text, count);
      node.textContent =
        count < all.length ? `${cut}… Read more` : alwaysToggle ? `${cut} Read more` : cut;
      return node.offsetHeight <= room;
    };

    const count = fittingWords(all.length, fits);
    node.textContent = '';
    setFit({ text: cutToWords(text, count), truncated: count < all.length });
  }, [text, lines, alwaysToggle]);

  return {
    text: open ? text : fit.text,
    truncated: fit.truncated,
    probeRef: (node) => {
      probe.current = node;
    },
  };
}
